// The console's *Reminders* area (CR-066: "TA Admin configuration without code: … reminders …"; G §11 "Reminder and
// limited escalation"; B10 plan D14, slice S20b; acceptance A-38, A-11). Registered for the kinds `metrics` and
// `escalation_policy`. For `metrics` it edits only the reminder rows — a definition's `reminder:` block, or a clock
// whose lapse is itself the reminder (`*.remind*`) — and the reminder suppression window; every other number of the
// metric table stays as it is in the version in effect, and the Journey (S20a) validates the whole body. A timer armed
// after the new version is effective carries its number (`timer.config_version`); one armed before keeps its own
// ("Given/When/Then: historical records retain configuration version used."). `escalation_policy` is read here, never
// edited: "Do not auto-escalate to HM manager." is a locked value (`auto_escalate_to_hm_manager`).

import { Banner, EmptyState, Flex, G6_LABELS, SLA_TIER_LABELS, Stack, SURFACE_LABELS, Table, Tag, Td, Th, Tr } from "@/mc";
import { CONSOLE_AREA_LABELS } from "./labels";
import { EditorFrame, NumberField, atPath, useDraft, useEffectiveBody, type Body } from "./ThresholdsEditor";
import { hsKey } from "@/views/b10/lib/hsKey";

type Path = readonly (string | number)[];
type Definition = Record<string, unknown> & { reminder?: Record<string, unknown>; occurrences?: Record<string, unknown>[] };

export interface ReminderField {
  path: Path;
  /** The body key the number sits under (`reminder.at_business_days`, `hours`, `occurrences.0.business_days_before`). */
  name: string;
  value: number;
}

export interface ReminderRow {
  id: string;
  key: string;
  owner: string;
  fields: ReminderField[];
}

/** The keys of a `reminder:` block that time it. */
const REMINDER_KEYS = ["at_business_days", "business_days_before", "hours_before", "hours"] as const;
/** The keys that time a clock whose own lapse is the reminder. */
const TIMING_KEYS = ["business_days", "hours", "hours_before", "days"] as const;
const OCCURRENCE_KEYS = ["business_days_before", "business_days", "hours_before"] as const;

const reminds = (tool: unknown): boolean => typeof tool === "string" && /\.remind/.test(tool);

/** A reminder row: the definition carries a `reminder:` block, or its lapse (its own or an occurrence's) reminds. */
export function isReminderDefinition(def: Definition): boolean {
  if (def.reminder && typeof def.reminder === "object") return true;
  return reminds(def["on_lapse"]) || (def.occurrences ?? []).some((occurrence) => reminds(occurrence?.["on_lapse"]));
}

/** The reminder rows of a metrics body and the numbers that time each one, in the table's order. */
export function reminderRows(body: Body | null): ReminderRow[] {
  const definitions = (atPath(body, ["definitions"]) ?? []) as Definition[];
  return definitions.flatMap((def, index) => {
    if (!isReminderDefinition(def)) return [];
    const fields: ReminderField[] = [];
    const push = (path: Path, name: string, value: unknown) => {
      if (typeof value === "number") fields.push({ path, name, value });
    };
    if (def.reminder && typeof def.reminder === "object") {
      for (const key of REMINDER_KEYS) push(["definitions", index, "reminder", key], `reminder.${key}`, def.reminder[key]);
    } else {
      if (reminds(def["on_lapse"])) for (const key of TIMING_KEYS) push(["definitions", index, key], key, def[key]);
      (def.occurrences ?? []).forEach((occurrence, at) => {
        if (!reminds(occurrence?.["on_lapse"] ?? def["on_lapse"])) return;
        for (const key of OCCURRENCE_KEYS) push(["definitions", index, "occurrences", at, key], `occurrences.${at}.${key}`, occurrence?.[key]);
      });
    }
    return [{ id: String(def["id"] ?? ""), key: String(def["key"] ?? ""), owner: String(def["owner"] ?? ""), fields }];
  });
}

export const SUPPRESSION_PATH: Path = ["worker", "reminder_suppression_hours"];

export function MetricsRemindersForm({ body, set }: { body: Body | null; set: (path: Path, value: unknown) => void }) {
  const rows = reminderRows(body);
  return (
    <Stack>
      <Banner tone="info" icon="bell">{CONSOLE_AREA_LABELS.reminderAndLimitedEscalation}</Banner>
      <NumberField
        label={<span className="mc-mono">{SUPPRESSION_PATH.join(".")}</span>}
        value={atPath(body, SUPPRESSION_PATH) as number | undefined}
        min={0}
        onChange={(n) => set(SUPPRESSION_PATH, Math.round(n))}
        
      />
      <Table
        dense
        label={G6_LABELS.reminders}
        empty={rows.length === 0 ? <EmptyState inline>—</EmptyState> : undefined}
        head={<Tr><Th>{G6_LABELS.reminders}</Th><Th>{SURFACE_LABELS.owner}</Th><Th>{SLA_TIER_LABELS.due}</Th></Tr>}
      >
        {rows.map((row) => (
          <Tr key={`${row.id}-${row.key}`}>
            <Td label={G6_LABELS.reminders} sub={<span className="mc-mono">{row.key}</span>}><span className="mc-mono">{row.id}</span></Td>
            <Td mono label={SURFACE_LABELS.owner}>{row.owner}</Td>
            <Td label={SLA_TIER_LABELS.due}>
              <Stack gap={6}>
                {row.fields.map((field) => (
                  <NumberField
                    key={field.name}
                    label={<span className="mc-mono">{field.name}</span>}
                    value={field.value}
                    min={0}
                    onChange={(n) => set(field.path, n)}
                    hs={hsKey("cfg.reminders", row.key, field.name)}
                  />
                ))}
              </Stack>
            </Td>
          </Tr>
        ))}
      </Table>
    </Stack>
  );
}

/** The SLA tier a rule is about, in his words. */
export const TIER_WORD: Readonly<Record<string, string>> = {
  half: SLA_TIER_LABELS.reminder,
  near_breach: SLA_TIER_LABELS.nearBreach,
  breached: SLA_TIER_LABELS.breach,
};

type Rule = { clock?: string; tier?: string; notify?: { to?: string; effect?: string }[] };

/** Who hears about a late clock, read-only: the allowed recipients and each rule (DC-24: values, not controls). */
export function EscalationPolicyView({ body }: { body: Body | null }) {
  const recipients = (atPath(body, ["recipients_allowed"]) ?? []) as string[];
  const rules = (atPath(body, ["rules"]) ?? []) as Rule[];
  return (
    <Stack>
      <Banner tone="info" icon="lock">{CONSOLE_AREA_LABELS.noHmManagerEscalation}</Banner>
      <Flex wrap>{recipients.map((recipient) => <Tag key={recipient}><span className="mc-mono">{recipient}</span></Tag>)}</Flex>
      <Table
        dense
        label={CONSOLE_AREA_LABELS.reminderAndLimitedEscalation}
        empty={rules.length === 0 ? <EmptyState inline>—</EmptyState> : undefined}
        head={<Tr><Th>{CONSOLE_AREA_LABELS.sla}</Th><Th>{SLA_TIER_LABELS.due}</Th><Th>{CONSOLE_AREA_LABELS.notification}</Th></Tr>}
      >
        {rules.map((rule, index) => (
          <Tr key={`${rule.clock}-${rule.tier}-${index}`}>
            <Td mono label={CONSOLE_AREA_LABELS.sla}>{rule.clock}</Td>
            <Td label={SLA_TIER_LABELS.due}>{TIER_WORD[rule.tier ?? ""] ?? <span className="mc-mono">{rule.tier}</span>}</Td>
            <Td mono label={CONSOLE_AREA_LABELS.notification}>{(rule.notify ?? []).map((n) => `${n.to} → ${n.effect}`).join(" · ")}</Td>
          </Tr>
        ))}
      </Table>
    </Stack>
  );
}

/** The area's editor for one key of `metrics` (its reminder rows) or `escalation_policy` (read-only). */
export function RemindersView({ kind, configKey }: { kind: string; configKey: string }) {
  const q = useEffectiveBody(configKey);
  const draft = useDraft(q.data?.body);
  const readOnly = kind !== "metrics";
  return (
    <EditorFrame
      kind={kind}
      configKey={configKey}
      title={G6_LABELS.reminders}
      version={q.data?.version}
      loading={q.isLoading}
      error={q.error}
      onRetry={() => void q.refetch()}
      draft={draft.current}
      readOnly={readOnly}
      onDone={() => { draft.reset(); void q.refetch(); }}
    >
      {kind === "metrics" ? <MetricsRemindersForm body={draft.current} set={draft.set} />
        : kind === "escalation_policy" ? <EscalationPolicyView body={draft.current} />
        : <EmptyState inline>—</EmptyState>}
    </EditorFrame>
  );
}
